import {observable, toJS} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import * as ReportAPI from '../../apis/ReportAPI';
import {getLanguage} from '../../i18n/i18n';
import {serialize} from '../../utility/ApiParamConversion';
import fileDownload from 'js-file-download';
import {SelectorTableColumnDef} from '../../stores/GeneralSelectorTableStore';
import {AnalyticsTab} from '../../stores/store';
import {DateRange} from '../../stores/TimeIntervalStore';
import {DateRangeWithId, Report, ReportGenerationModalPackage, TextVariableWithValue} from '../types/Report';
import {ReportEditorState} from './ReportEditorStore';
import ReportSerializer from '../utility/ReportSerializer';
import ReportDeserializer from '../utility/ReportDeserializer';
import {handleError} from '../../apis/errorHandling';

export interface ReportState {
    loadReports: () => void;
    generateReport: (idReport: number, dateRangesWithId: DateRangeWithId[], textVariablesWithValue: TextVariableWithValue[]) => void;
    openReportGenerationModal: (report: Report) => void;
    closeReportGenerationModal: () => void;
    updateDateRangeRegisterForReportGenerationModal: (idNamedDateRange: string, dateRange: DateRange) => void;
    updateTextVariableValue: (textVariableName: string, textVariableValue: string) => void;
    availableReports: Report[];
    removeReport: (id: number) => Promise<void>;
    saveNewReport: (report: Report) => Promise<void>;
    saveExistingReport: (report: Report, force?: boolean) => Promise<void>;
    setReportRemovalModal: (idReport?: number) => void;
    reportIsGettingDeleted: boolean;
    defaultReportModalIsOpen: boolean;
    defaultReports: Report[];
    downloadingReportsIds: number[];
}

interface MainStoreDepenencies {
    getMainDateRange: () => DateRange;
    getTargetCurrency: () => string | undefined;
    getTableColumnListWithChannelPerAnalyticsTab: (analyticsTab: AnalyticsTab) => SelectorTableColumnDef[];
}

export default class ReportStore implements ReportState {
    @observable public availableReports: Report[] = [];
    @observable public defaultReports: Report[] = [];
    @observable public downloadingReportsIds: number[] = [];
    @observable public generateReportModalIsOpenForReport?: ReportGenerationModalPackage;
    @observable public idReportToRemove?: number;
    @observable public reportIsGettingDeleted = false;
    @observable public defaultReportModalIsOpen = false;
    public reportEditorStore!: ReportEditorState;

    constructor(private mainStoreDependency: MainStoreDepenencies) {}

    public loadReports = () => {
        Promise.all([ReportAPI.getTemplates(), ReportAPI.getDefaultTemplates()])
            .then(([templatesResp, defaultTemplatesResp]) => {
                this.defaultReports = new ReportDeserializer(
                    this.mainStoreDependency.getTableColumnListWithChannelPerAnalyticsTab
                ).deserializeReports(defaultTemplatesResp.data);
                this.availableReports = new ReportDeserializer(
                    this.mainStoreDependency.getTableColumnListWithChannelPerAnalyticsTab
                ).deserializeReports(templatesResp.data);
            })
            .catch(handleError('error:reportDeserializationFailed'));
    };

    public generateReport = (idReport: number, dateRangesWithIds: DateRangeWithId[], textVariablesWithValue: TextVariableWithValue[]) => {
        this.downloadingReportsIds.push(idReport);

        const removeIdReportIdFromDownloads = (idReportToRemove: number) => {
            const matchingReportIdIndex = this.downloadingReportsIds.indexOf(idReportToRemove);
            if (matchingReportIdIndex > -1) {
                const downloadingReportIds = _.cloneDeep(toJS(this.downloadingReportsIds));
                downloadingReportIds.splice(matchingReportIdIndex, 1);
                this.downloadingReportsIds = downloadingReportIds;
            }
        };

        ReportAPI.downloadTemplateReport({
            id: idReport,
            language: getLanguage(),
            dates: dateRangesWithIds.map((dateRangeWithId) => serialize(dateRangeWithId, ['dateRange'])),
            variables: textVariablesWithValue,
        })
            .then((resp) => {
                removeIdReportIdFromDownloads(idReport);
                fileDownload(resp.data, 'report.odt');
            })
            .catch(() => {
                removeIdReportIdFromDownloads(idReport);
            });
    };

    public openReportGenerationModal = (report: Report) => {
        this.generateReportModalIsOpenForReport = {
            report,
            dateRangesWithId: report.dates.map((nameDateRange) => ({
                id: nameDateRange.id,
                dateRange: this.mainStoreDependency.getMainDateRange(),
            })),
            textVariablesWithValue: report.variables.map((textVariable) => ({
                name: textVariable,
                value: '',
            })),
        };
    };

    public closeReportGenerationModal = () => {
        this.generateReportModalIsOpenForReport = undefined;
    };

    public updateDateRangeRegisterForReportGenerationModal = (idNamedDateRange: string, dateRange: DateRange) => {
        if (this.generateReportModalIsOpenForReport) {
            const dateRangesWithId = _.cloneDeep(toJS(this.generateReportModalIsOpenForReport.dateRangesWithId)).filter(
                (dateRangeWithId) => dateRangeWithId.id !== idNamedDateRange
            );
            dateRangesWithId.push({
                dateRange: {
                    startDate: moment(dateRange.startDate).startOf('day'),
                    endDate: moment(dateRange.endDate).endOf('day'),
                },
                id: idNamedDateRange,
            });
            this.generateReportModalIsOpenForReport.dateRangesWithId = _.uniqBy(dateRangesWithId, 'id');
        } else {
            throw Error('generateReportModalIsOpenForReport is undefined, cannot add to generateReportModalIsOpenForReport.dateRangesWithId');
        }
    };

    public updateTextVariableValue = (textVariableName: string, textVariableValue: string) => {
        if (!this.generateReportModalIsOpenForReport) {
            throw Error('Cannot update textVariable value, if generateReportModalIsOpenForReport is not set');
        }
        const {textVariablesWithValue} = this.generateReportModalIsOpenForReport;
        const variableMatch = textVariablesWithValue.find((textVariableWithValue) => textVariableWithValue.name === textVariableName);
        if (!variableMatch) {
            throw Error('Could not find textVariable with name ' + textVariableName);
        }
        variableMatch.value = textVariableValue;
        this.generateReportModalIsOpenForReport.textVariablesWithValue = textVariablesWithValue;
    };

    public setReportRemovalModal = (idReport?: number): void => {
        this.idReportToRemove = idReport;
    };

    public removeReport = (idReport: number): Promise<void> => {
        this.reportIsGettingDeleted = true;
        return ReportAPI.removeTemplate(idReport)
            .then(() => {
                this.reportIsGettingDeleted = false;
                this.availableReports = this.availableReports.filter((availableReport) => availableReport.id !== idReport);
            })
            .catch(() => {
                this.reportIsGettingDeleted = false;
            });
    };

    public saveNewReport = (newReport: Report): Promise<void> => {
        return ReportAPI.createTemplate(new ReportSerializer(this.mainStoreDependency.getTargetCurrency).serializeReport(newReport)).then((resp) => {
            const {id, lastChanged} = resp.data;
            this.availableReports.push({...newReport, id, lastChanged});
        });
    };

    public saveExistingReport = (reportToUpdate: Report, force = false): Promise<void> => {
        return ReportAPI.updateTemplate(new ReportSerializer(this.mainStoreDependency.getTargetCurrency).serializeReport(reportToUpdate), force).then(
            (resp) => {
                reportToUpdate.lastChanged = resp.data.lastChanged;
                this.availableReports = [
                    ...this.availableReports.filter((availableReport) => availableReport.id !== reportToUpdate.id),
                    reportToUpdate,
                ];
            }
        );
    };
}



// WEBPACK FOOTER //
// ./src/report/stores/ReportStore.ts